/**
 * CreateInvestigation Component - Modal Form para criar investigação
 * Baseado em best practices 2025: Headless UI Dialog, inline validation, TypeScript
 *
 * Padrão:
 * - Validação inline (após blur do campo)
 * - Campos condicionais (CPF+Nome se tipo=cpf-nome)
 * - Máscaras de input (CNPJ, CPF, telefone)
 * - Loading state durante submit
 * - Toast notifications
 *
 * Referências:
 * - https://headlessui.com/react/dialog
 * - https://www.freecodecamp.org/news/how-to-validate-forms-in-react/
 * - https://medium.com/@farzanekazemi8517/best-practices-for-handling-forms-in-react-2025-edition-62572b14452f
 */

import { useState, FormEvent, ChangeEvent } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import type { TipoNo, CreateInvestigationPayload } from '../../types/investigation';
import { validateInvestigationForm } from '../../utils/validators';
import { formatCNPJ, formatCPF, formatTelefone } from '../../utils/formatters';

interface CreateInvestigationProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateInvestigationPayload) => Promise<void>;
  loading?: boolean;
}

interface FormData {
  tipo: TipoNo;
  valor: string;
  cpf: string;
  nome: string;
  profundidadeMaxima: number;
  autoExpandir: boolean;
}

const INITIAL_FORM_DATA: FormData = {
  tipo: 'cnpj',
  valor: '',
  cpf: '',
  nome: '',
  profundidadeMaxima: 8,
  autoExpandir: false,
};

/**
 * Modal com formulário para criar nova investigação
 *
 * Features:
 * - ✅ Validação inline por campo
 * - ✅ Campos condicionais (CPF+Nome)
 * - ✅ Máscaras automáticas
 * - ✅ Loading state
 * - ✅ Mobile-friendly (full-screen)
 * - ✅ Acessível (labels, aria-labels)
 *
 * @example
 * const { createInvestigation, loading } = useInvestigation();
 *
 * <CreateInvestigation
 *   isOpen={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   onSubmit={createInvestigation}
 *   loading={loading}
 * />
 */
export function CreateInvestigation({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}: CreateInvestigationProps) {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  /**
   * Handle input change com máscara automática
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    let formattedValue = value;

    // Aplicar máscaras baseado no tipo de campo
    if (name === 'valor') {
      if (formData.tipo === 'cnpj') {
        formattedValue = formatCNPJ(value);
      } else if (formData.tipo === 'telefone') {
        formattedValue = formatTelefone(value);
      }
    } else if (name === 'cpf') {
      formattedValue = formatCPF(value);
    }

    // Checkbox handling
    const finalValue = type === 'checkbox'
      ? (e.target as HTMLInputElement).checked
      : formattedValue;

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Limpar erro quando usuário começa a digitar
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  /**
   * Handle blur - validar campo ao sair
   */
  const handleBlur = (fieldName: string) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));

    // Validar apenas esse campo
    const fieldErrors = validateInvestigationForm({
      tipo: formData.tipo,
      valor: formData.valor,
      cpf: formData.cpf,
      nome: formData.nome,
      profundidadeMaxima: formData.profundidadeMaxima,
    });

    if (fieldErrors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: fieldErrors[fieldName],
      }));
    }
  };

  /**
   * Handle tipo change - limpa campos condicionais
   */
  const handleTipoChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newTipo = e.target.value as TipoNo;
    setFormData((prev) => ({
      ...prev,
      tipo: newTipo,
      valor: '', // Limpa valor quando muda tipo
      cpf: '',
      nome: '',
    }));
    setErrors({});
    setTouched({});
  };

  /**
   * Handle submit
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validar todos os campos
    const formErrors = validateInvestigationForm({
      tipo: formData.tipo,
      valor: formData.valor,
      cpf: formData.cpf,
      nome: formData.nome,
      profundidadeMaxima: formData.profundidadeMaxima,
    });

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setTouched({
        tipo: true,
        valor: true,
        cpf: true,
        nome: true,
      });
      return;
    }

    // Montar payload
    const payload: CreateInvestigationPayload = {
      tipo: formData.tipo,
      valor: formData.valor.replace(/\D/g, ''), // Remove formatação
      profundidadeMaxima: formData.profundidadeMaxima,
      autoExpandir: formData.autoExpandir,
    };

    // Se tipo CPF-Nome, adicionar campos
    if (formData.tipo === 'cpf-nome') {
      payload.cpf = formData.cpf.replace(/\D/g, '');
      payload.nome = formData.nome;
    }

    try {
      await onSubmit(payload);
      // Reset form
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setTouched({});
      onClose();
    } catch (error) {
      // Erro já tratado no parent via useInvestigation onError
    }
  };

  /**
   * Handle close modal
   */
  const handleClose = () => {
    if (!loading) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setTouched({});
      onClose();
    }
  };

  // Labels por tipo
  const getValorLabel = (): string => {
    switch (formData.tipo) {
      case 'cnpj': return 'CNPJ';
      case 'email': return 'Email';
      case 'telefone': return 'Telefone';
      case 'cpf-nome': return 'CPF + Nome';
      default: return 'Valor';
    }
  };

  const getValorPlaceholder = (): string => {
    switch (formData.tipo) {
      case 'cnpj': return '00.000.000/0000-00';
      case 'email': return 'exemplo@email.com';
      case 'telefone': return '(00) 00000-0000';
      case 'cpf-nome': return 'Busca por CPF e Nome';
      default: return '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nova Investigação"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Tipo de entrada */}
        <div>
          <label htmlFor="tipo" className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Entrada <span className="text-red-500">*</span>
          </label>
          <select
            id="tipo"
            name="tipo"
            value={formData.tipo}
            onChange={handleTipoChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px] text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            <option value="cnpj">CNPJ (Empresa)</option>
            <option value="email">Email</option>
            <option value="telefone">Telefone</option>
            <option value="cpf-nome">CPF + Nome</option>
          </select>
        </div>

        {/* Valor principal (condicional - não mostrar se tipo=cpf-nome) */}
        {formData.tipo !== 'cpf-nome' && (
          <Input
            label={getValorLabel()}
            name="valor"
            type={formData.tipo === 'email' ? 'email' : 'text'}
            value={formData.valor}
            onChange={handleChange}
            onBlur={() => handleBlur('valor')}
            placeholder={getValorPlaceholder()}
            error={touched.valor ? errors.valor : undefined}
            required
            disabled={loading}
          />
        )}

        {/* Campos CPF + Nome (condicional) */}
        {formData.tipo === 'cpf-nome' && (
          <>
            <Input
              label="CPF"
              name="cpf"
              type="text"
              value={formData.cpf}
              onChange={handleChange}
              onBlur={() => handleBlur('cpf')}
              placeholder="000.000.000-00"
              error={touched.cpf ? errors.cpf : undefined}
              required
              disabled={loading}
            />
            <Input
              label="Nome Completo"
              name="nome"
              type="text"
              value={formData.nome}
              onChange={handleChange}
              onBlur={() => handleBlur('nome')}
              placeholder="Nome da pessoa"
              error={touched.nome ? errors.nome : undefined}
              required
              disabled={loading}
            />
          </>
        )}

        {/* Profundidade máxima */}
        <div>
          <label htmlFor="profundidadeMaxima" className="block text-sm font-medium text-gray-700 mb-1">
            Profundidade Máxima
          </label>
          <select
            id="profundidadeMaxima"
            name="profundidadeMaxima"
            value={formData.profundidadeMaxima}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px] text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            {[3, 5, 8, 10].map((depth) => (
              <option key={depth} value={depth}>
                {depth} {depth === 8 && '(Recomendado)'}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Número máximo de níveis de expansão do grafo
          </p>
        </div>

        {/* Auto-expandir */}
        <div className="flex items-center">
          <input
            id="autoExpandir"
            name="autoExpandir"
            type="checkbox"
            checked={formData.autoExpandir}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            disabled={loading}
          />
          <label htmlFor="autoExpandir" className="ml-2 text-sm text-gray-700">
            Expandir automaticamente todos os nós
          </label>
        </div>

        {/* Aviso */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <p className="text-xs sm:text-sm text-yellow-800">
            <strong>Atenção:</strong> Investigações com alta profundidade ou auto-expansão podem demorar vários minutos.
          </p>
        </div>

        {/* Botões - Mobile: stack, Desktop: inline */}
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={handleClose}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={loading}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {loading ? 'Criando...' : 'Iniciar Investigação'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
